// ============= Imports =============
import { AuthContextProps, tokenManager } from '@tw/tw-component-library';
import { ApiResponse } from '../types';

// ============= Types =============
interface UploadDocumentParams {
  file: File;
  imageName: string;
  billNumber: string;
  getAccessToken: AuthContextProps['getAccessToken'];
}

interface UploadResponse {
  success: boolean;
  message: string;
  data?: any;
}

// ============= Constants =============
const IS_DEVELOPMENT = import.meta.env.MODE === 'development';
const PROD_BASE_URL = import.meta.env.VITE_REQUEST_URL;
const API_ENDPOINT = '/api/savedocument';
const SIDETRADE_SCOPE =
  'api://b922470e-04e2-4c04-84fc-e5ec591459ad/SidetradeFrontEndProd';
const HEADERS = {
  'x-functions-key': 'Test',
};

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

const constructUploadUrl = (imageName: string, billNumber: string): string => {
  const queryParams = `?imageName=${encodeURIComponent(
    imageName
  )}&billNumber=${encodeURIComponent(billNumber)}`;

  if (IS_DEVELOPMENT) {
    // For local development with proxy
    return `${API_ENDPOINT}${queryParams}`;
  } else {
    // For production
    return `${PROD_BASE_URL}${API_ENDPOINT}${queryParams}`;
  }
};

// ============= Main Upload Function =============
export const uploadDocument = async ({
  file,
  imageName,
  billNumber,
  getAccessToken,
}: UploadDocumentParams): Promise<ApiResponse<UploadResponse>> => {
  try {
    console.log(
      `🔄 Uploading document in ${
        IS_DEVELOPMENT ? 'development' : 'production'
      } mode...`
    );

    const url = constructUploadUrl(imageName, billNumber);
    console.log('🌐 Upload URL:', url);

    // Token acquisition with required scopes
    const token = await getAccessToken([SIDETRADE_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire Graph API token');
    }
    tokenManager.logTokenInfo(token, 'Graph API');

    const formData = new FormData();
    formData.append('file', file);

    const headers = createHeaders(token);

    const fetchOptions: RequestInit = {
      method: 'POST',
      headers,
      body: formData,
      ...(IS_DEVELOPMENT && { credentials: 'include' }),
    };

    console.log('📤 Uploading file:', file.name);
    console.log('Request options:', {
      url,
      method: fetchOptions.method,
      headers: fetchOptions.headers,
      credentials: fetchOptions.credentials,
    });

    const response = await fetch(url, fetchOptions);

    console.log('Response status:', response.status);
    console.log('Response headers:', Object.fromEntries(response.headers));

    // Get the response as text first
    const responseText = await response.text();
    console.log('Response text:', responseText);

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${responseText}`);
    }

    // Try to parse as JSON if possible, otherwise use text response
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      // If it's not JSON, create a simple success object with the text
      data = {
        message: responseText,
        status: response.status,
      };
    }

    console.log('✅ Document uploaded successfully');

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('🔴 Error uploading document:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed',
    };
  }
};
